import type { ThreeNS } from "../../wakbbu/engine3d/three";

/**
 * The 말랑이's shape lives in the vertex shader, on a plain unit sphere mesh. Every vertex is a unit direction u of
 * the toy's material (where that bit of skin sits when nothing touches it), and the shader places it:
 *   the body  an egg (a long ellipsoid, a little narrower toward its +x end, like the mango squishy),
 *   global    the hands' affine squeeze (MallangEngine.globalMatrix is the CPU copy, used to pick): squashed along
 *             the axis between two fingers closing on it and fattened across it, then the one-sided squash of the
 *             press toward the screen,
 *   dents     in that deformed space, along the direction from the centre: one per finger and one per mark still
 *             filling back in — a fingertip pad sunk by D (negative = bulging out while it springs back), a lip of
 *             squeezed-up skin round it, and the little pull of skin still clinging to a lifted finger; plus the
 *             swell (the volume pressed in comes out elsewhere) and the body's jiggle (a P2 mode),
 *   sag       the side toward gravity hangs a little (phone tilt),
 *   grabs     like mozzarella: a finger that takes hold draws the whole toy out toward it — a ramp along the pull
 *             from its far end (which stays put) to the grabbed point (which follows the finger by Δ) — and the
 *             middle of the stretch thins into a strand that sags with gravity. Two fingers split it in halves
 *             instead (each half follows its finger), so pulling them apart draws it out into taffy. (A column
 *             under the finger, w > 0 below, is the fallback before a pull has a direction.)
 * Normals are central differences of that same function. The material direction also goes to the fragment stage,
 * for the skin's faint texture: it stretches and squashes with the skin.
 */

export const MAX_DENTS = 8;
/** fingers pulling, and pulled chunks still drawing back in */
export const MAX_GRABS = 6;

export type MallangUniforms = Record<string, { value: unknown }>;

export function makeMallangUniforms(T: ThreeNS): MallangUniforms {
  const a: any[] = [], b: any[] = [];
  for (let i = 0; i < MAX_DENTS; i++) { a.push(new T.Vector4(0, 0, 1, 0)); b.push(new T.Vector4(0.3, 0, 0, 0)); }
  const ga: any[] = [], gb: any[] = [], gc: any[] = [];
  for (let i = 0; i < MAX_GRABS; i++) { ga.push(new T.Vector4(0, 0, 1, 0.5)); gb.push(new T.Vector4(0, 0, 0, 0)); gc.push(new T.Vector4(0, 0, 1, 0)); }
  return {
    uDA: { value: a }, // xyz = direction (deformed space), w = depth D
    uDB: { value: b }, // x = sigma (rad), y = flat (0 gaussian … 1 fingertip pad), z = lip strength, w = clinging pull
    uDN: { value: 0 },
    // xyz = the view direction when it was grabbed (toy frame): the chunk is the column under the finger, w = its
    // radius (radius units); or, for a hold in two fingers (w < 0), xyz = the axis toward this finger's half and
    // −w = how soft the split between the halves is
    uGA: { value: ga },
    uGB: { value: gb }, // xyz = Δ, how far it is pulled out (toy frame, radius units), w = neck thinning 0..1
    uGC: { value: gc }, // xyz = the grabbed point on the surface (two fingers: the midpoint between them), w = how far the neck sags
    uGN: { value: 0 },
    uDown: { value: new T.Vector3(0, -1, 0) }, // which way a hanging strand sags (toy frame)
    uAxes: { value: new T.Vector3(1.22, 0.86, 0.86) },
    uEgg: { value: 0.12 },
    uPA: { value: new T.Vector4(1, 0, 0, 0) }, // xyz = pinch axis, w = k (>0 squeezed along it)
    uSq: { value: new T.Vector3(0, 0, 1) },
    uSPar: { value: 1 },
    uSPerp: { value: 1 },
    uVol: { value: 0 },
    uWob: { value: new T.Vector4(1, 0, 0, 0) }, // xyz = axis, w = amplitude
    uGrav: { value: new T.Vector4(0, -1, 0, 0) }, // xyz = sag direction (toy frame), w = amount
    uBumpH: { value: 0.001 }, // the skin texture's height, world units
  };
}

const VERT_PARS = /* glsl */ `
uniform vec4 uDA[${MAX_DENTS}];
uniform vec4 uDB[${MAX_DENTS}];
uniform int uDN;
uniform vec4 uGA[${MAX_GRABS}];
uniform vec4 uGB[${MAX_GRABS}];
uniform vec4 uGC[${MAX_GRABS}];
uniform int uGN;
uniform vec3 uDown;
uniform vec3 uAxes;
uniform float uEgg;
uniform vec4 uPA;
uniform vec3 uSq;
uniform float uSPar;
uniform float uSPerp;
uniform float uVol;
uniform vec4 uWob;
uniform vec4 uGrav;
varying float vMalAO;
varying vec3 vMalMat;
// the hands' squeeze (an affine map; MallangEngine.globalMatrix is its CPU copy)
vec3 malGlobal(vec3 p) {
  p = p * (1.0 + 0.5 * uPA.w) - uPA.xyz * (1.5 * uPA.w * dot(p, uPA.xyz));
  float al = dot(p, uSq);
  return uSq * ((al + 1.0) * uSPar - 1.0) + (p - uSq * al) * uSPerp;
}
// the radial scale along direction d (deformed space): dents, their lips, the clinging pull, swell, wobble
float malScale(vec3 d, out float ao) {
  float s = 1.0;
  ao = 0.0;
  for (int i = 0; i < ${MAX_DENTS}; i++) {
    if (i >= uDN) break;
    vec4 a = uDA[i], b = uDB[i];
    float cd = clamp(dot(d, a.xyz), -1.0, 1.0), ang = acos(cd);
    float sg = max(b.x, 1e-3), x = ang / sg;
    float g = mix(exp(-0.5 * x * x), exp(-0.5 * pow(x, 2.6)), b.y);
    float q = (ang - 1.7 * sg) / (0.55 * sg);
    float xp = ang / (0.45 * sg);
    s += -a.w * g + a.w * b.z * exp(-0.5 * q * q) + b.w * exp(-0.5 * xp * xp);
    ao += max(a.w, 0.0) * g;
  }
  float c = dot(d, uWob.xyz);
  s += uVol * (1.0 - 0.45 * dot(d, uSq)) + uWob.w * (1.5 * c * c - 0.5);
  return max(s, 0.3);
}
// the surface point of material direction u (the toy's frame)
vec3 malPoint(vec3 u, out float ao) {
  vec3 p = u * uAxes;
  p.yz *= 1.0 - uEgg * u.x; // narrower toward the +x end
  vec3 q = malGlobal(p);
  float r = length(q);
  vec3 d = q / max(r, 1e-5);
  q *= malScale(d, ao);
  // the side toward gravity hangs a little lower
  q += uGrav.xyz * (uGrav.w * 0.5 * (1.0 + dot(d, uGrav.xyz)) * r);
  // grabs. One finger: the whole body is drawn toward it like a pulled rice cake — the patch under the finger comes
  // along with it, the material behind gives more the nearer the finger it is (a strand tapering to the finger),
  // and everything thins toward the strand's line as it stretches (the body slims to feed the strand) and sags.
  // Two fingers: the toy splits in halves at the midpoint, each following its finger (taffy, thin in the middle).
  vec3 q0 = q;
  for (int i = 0; i < ${MAX_GRABS}; i++) {
    if (i >= uGN) break;
    vec4 ga = uGA[i], gb = uGB[i], gc = uGC[i];
    float L = length(gb.xyz);
    if (L < 1e-5) continue;
    vec3 e = q0 - gc.xyz;
    if (ga.w > 0.0) {
      // gc: the grabbed point and the radius of the core the finger holds; ga: the view direction and the distance
      // from the core's edge to the far side; gb: the pull and the sag. Distances are taken as seen on the screen, so
      // the toy's whole thickness under the finger comes along together (a strand with a round section, not a skin
      // pulled off the front). The give falls off with that distance — most of it right behind the finger, so the
      // strand tapers down to the core — except ahead of the finger along the pull, where it falls ~5× more slowly:
      // what lies ahead has nowhere to go but along with it (any faster and the core overtakes it and the skin
      // folds), and it comes along as the strand's tip, shrunk the same along and across.
      vec3 a = gb.xyz / L, v = ga.xyz;
      vec3 es = e - v * dot(e, v);
      float al = dot(es, a);
      vec3 ep = es - a * al;
      float ah = al > 0.0 ? al * 0.18 : al;
      // (the core is a soft peak, not a flat disc: a disc's edge ends the strand in a flat-topped nub)
      float ds = sqrt(dot(ep, ep) + ah * ah + gc.w * gc.w) - gc.w;
      float wb = 1.0 - clamp(ds / ga.w, 0.0, 1.0);                  // 1 under the finger, 0 at the far side
      float f = wb * wb * wb;
      float t = 1.0 - inversesqrt(1.0 + (L / ga.w) * 3.0 * wb * wb); // the stretch keeps its volume: it thins
      vec3 qn = q0 + gb.xyz * f;
      vec3 et = qn - gc.xyz - gb.xyz;
      vec3 perp = et - a * dot(et, a);                              // off the pull's line (through the finger)
      // the strand hangs under its own weight between the body and the finger, most in its middle — only what has
      // drawn thin (near the pull's line), and by where it ends up along the pull, so its front and back sag together.
      // A strand pulled downward hangs already: only a pull running across gravity sags (and a diagonal one less,
      // or the sag would crease the body where the strand leaves it)
      vec3 ps = perp - v * dot(perp, v);
      float pq = length(ps) * (1.0 - t);
      float sa = clamp(dot(qn - gc.xyz, a) / L, 0.0, 1.0);
      float across = 1.0 - dot(uDown, a) * dot(uDown, a);
      float hang = (0.5 - 0.5 * cos(6.2831853 * sa)) * exp(-4.0 * pq * pq) * across * across;
      q += (qn - q0) - perp * t + uDown * (gb.w * hang);
      ao += 0.3 * t;
    } else {
      float along = dot(e, ga.xyz);
      // the ramp between the fingers: smootherstep, so the bend where it starts has no curvature jump (a smoothstep
      // shows a crease line in the shading there)
      float t = clamp(0.5 + 0.5 * along / -ga.w, 0.0, 1.0);
      float w = t * t * t * (t * (t * 6.0 - 15.0) + 10.0);
      float neck = 4.0 * w * (1.0 - w);           // the stretch between the two halves
      vec3 ax = gb.xyz / L;
      vec3 ep = e - ax * dot(e, ax);
      q += gb.xyz * w - ep * (gb.w * neck) + uDown * (gc.w * neck);
      ao += 0.25 * gb.w * neck;
    }
  }
  return q;
}
`;

const VERT_SHAPE = /* glsl */ `
vec3 malU = normalize(position);
float malAo, malAo2;
vec3 malP = malPoint(malU, malAo);
vec3 malUp = abs(malU.y) < 0.99 ? vec3(0.0, 1.0, 0.0) : vec3(1.0, 0.0, 0.0);
vec3 malT1 = normalize(cross(malUp, malU)), malT2 = cross(malU, malT1);
const float malE = 0.012;
vec3 malN = normalize(cross(
  malPoint(normalize(malU + malT1 * malE), malAo2) - malPoint(normalize(malU - malT1 * malE), malAo2),
  malPoint(normalize(malU + malT2 * malE), malAo2) - malPoint(normalize(malU - malT2 * malE), malAo2)));
if (dot(malN, malP) < 0.0) malN = -malN;
objectNormal = malN;
vMalAO = malAo;
vMalMat = malU;
`;

/**
 * Soft silicone: the light wraps a little past the shadow line and the colour deepens there (the toy is slightly
 * translucent), the hollow of a deep press darkens (less of the room reaches into it). The skin has a faint, fine
 * orange-peel texture and a slight unevenness of tone, both fixed to the material (vMalMat), so they stretch with it.
 * Specular, clearcoat and sheen are three's own.
 */
const FRAG_PARS = /* glsl */ `
varying float vMalAO;
varying vec3 vMalMat;
uniform float uBumpH;
float mlHash(vec3 c) {
  uvec3 q = uvec3(ivec3(floor(c)) + 4096);
  uint h = (q.x * 1597334677u) ^ (q.y * 3812015801u) ^ (q.z * 2798796415u);
  h ^= h >> 16; h *= 2246822519u; h ^= h >> 13; h *= 3266489917u; h ^= h >> 16;
  return float(h) * (1.0 / 4294967296.0);
}
float mlNoise(vec3 p) {
  vec3 i = floor(p), f = p - i, u = f * f * (3.0 - 2.0 * f);
  float a = mlHash(i), b = mlHash(i + vec3(1.0, 0.0, 0.0)), c = mlHash(i + vec3(0.0, 1.0, 0.0)), d = mlHash(i + vec3(1.0, 1.0, 0.0));
  float e = mlHash(i + vec3(0.0, 0.0, 1.0)), g = mlHash(i + vec3(1.0, 0.0, 1.0)), k = mlHash(i + vec3(0.0, 1.0, 1.0)), l = mlHash(i + vec3(1.0, 1.0, 1.0));
  return mix(mix(mix(a, b, u.x), mix(c, d, u.x), u.y), mix(mix(e, g, u.x), mix(k, l, u.x), u.y), u.z);
}
// the skin's micro relief as a normal tilt (screen derivatives of its height)
vec3 mlSkin(vec3 n) {
  float h = ((mlNoise(vMalMat * 52.0) - 0.5) + 0.35 * (mlNoise(vMalMat * 131.0) - 0.5)) * uBumpH;
  vec3 px = dFdx(-vViewPosition), py = dFdy(-vViewPosition);
  float lx = max(length(px), 1e-6), ly = max(length(py), 1e-6);
  vec3 g = (dFdx(h) / lx) * (px / lx) + (dFdy(h) / ly) * (py / ly);
  g -= n * dot(g, n);
  return normalize(n - g);
}
void RE_Direct_Mallang(const in IncidentLight directLight, const in vec3 geometryPosition, const in vec3 geometryNormal, const in vec3 geometryViewDir, const in vec3 geometryClearcoatNormal, const in PhysicalMaterial material, inout ReflectedLight reflectedLight) {
  ReflectedLight l = ReflectedLight(vec3(0.0), vec3(0.0), vec3(0.0), vec3(0.0));
  RE_Direct_Physical(directLight, geometryPosition, geometryNormal, geometryViewDir, geometryClearcoatNormal, material, l);
  reflectedLight.directSpecular += l.directSpecular;
  float nl = dot(geometryNormal, directLight.direction);
  const float w = 0.45;
  float lam = saturate(nl), wrap = saturate((nl + w) / (1.0 + w));
  vec3 c = material.diffuseColor;
  vec3 deep = mix(c, c * c / max(max(max(c.r, c.g), c.b), 0.04), 0.6);
  reflectedLight.directDiffuse += directLight.color * (BRDF_Lambert(c) * lam * 0.92 + BRDF_Lambert(deep) * 0.7 * (wrap - lam));
}
#undef RE_Direct
#define RE_Direct RE_Direct_Mallang
`;

export function mallangOnBeforeCompile(this: { mallangUniforms?: MallangUniforms }, shader: { uniforms: Record<string, unknown>; vertexShader: string; fragmentShader: string }) {
  if (this.mallangUniforms) Object.assign(shader.uniforms, this.mallangUniforms);
  let vs = shader.vertexShader;
  vs = vs.replace("#include <common>", "#include <common>\n" + VERT_PARS);
  vs = vs.replace("#include <beginnormal_vertex>", "#include <beginnormal_vertex>\n" + VERT_SHAPE);
  vs = vs.replace("#include <begin_vertex>", "#include <begin_vertex>\ntransformed = malP;");
  shader.vertexShader = vs;
  let fs = shader.fragmentShader;
  fs = fs.replace("#include <lights_physical_pars_fragment>", "#include <lights_physical_pars_fragment>\n" + FRAG_PARS);
  // the hollow of a deep press: a little darker; the skin's tone a touch uneven
  fs = fs.replace(
    "#include <color_fragment>",
    "#include <color_fragment>\ndiffuseColor.rgb *= (1.0 - 0.55 * clamp(vMalAO * 2.2, 0.0, 0.6)) * (0.97 + 0.06 * mlNoise(vMalMat * 7.0));",
  );
  fs = fs.replace(
    "#include <normal_fragment_maps>",
    "#include <normal_fragment_maps>\nnormal = mlSkin(normal);",
  );
  fs = fs.replace(
    "#include <clearcoat_normal_fragment_maps>",
    "#include <clearcoat_normal_fragment_maps>\n#ifdef USE_CLEARCOAT\nclearcoatNormal = mlSkin(clearcoatNormal);\n#endif",
  );
  // less of the room's light gets into a deep press
  fs = fs.replace("#include <aomap_fragment>", "#include <aomap_fragment>\nfloat malOcc = 1.0 - clamp(vMalAO * 2.5, 0.0, 0.55);\nreflectedLight.indirectDiffuse *= malOcc;\nreflectedLight.indirectSpecular *= mix(1.0, malOcc, 0.6);");
  shader.fragmentShader = fs;
}
